import { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { movies, categories, categoryMap } from '@/mocks/movies';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const currentSlug = slug || 'all';

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [currentSlug]);

  const filteredMovies = currentSlug === 'all'
    ? movies
    : movies.filter(m => categoryMap[currentSlug] && m.category === categoryMap[currentSlug]);

  const currentCategory = categories.find(c => c.slug === currentSlug);

  const handleCategoryChange = (newSlug: string) => {
    navigate(`/category/${newSlug}`);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <>
      <Navbar />

      <main className="min-h-screen">
        <div className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '电影分类' }
              ]}
            />
          </div>
        </div>

        <section className="py-8 md:py-12 bg-background-100/50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">电影分类</h1>
            <p className="text-sm text-foreground-500 mb-8">按类型筛选，精准找到你想看的电影</p>

            {/* Category Tabs */}
            <div className="flex flex-wrap gap-2 mb-10">
              {categories.map(cat => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    currentSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:bg-background-200/70 border border-background-200/70'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            {filteredMovies.length > 0 ? (
              <>
                <div className="flex items-center gap-2 mb-6">
                  <span className="text-sm text-foreground-500">
                    当前分类：
                  </span>
                  <span className="text-sm font-semibold text-foreground-900">
                    {currentCategory?.name || '全部'}
                  </span>
                  <span className="text-sm text-foreground-400">
                    · 共 {filteredMovies.length} 部电影
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
                  {filteredMovies.map(movie => (
                    <MovieCard
                      key={movie.id}
                      id={movie.id}
                      slug={movie.slug}
                      title={movie.title}
                      year={movie.year}
                      category={movie.category}
                      rating={movie.rating}
                      posterUrl={movie.posterUrl}
                      isVip={movie.isVip}
                      isNew={movie.isNew}
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <span className="w-16 h-16 flex items-center justify-center rounded-2xl bg-background-200 text-foreground-400 mx-auto mb-4">
                  <i className="ri-movie-line text-2xl"></i>
                </span>
                <p className="text-foreground-500">该分类暂无电影</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": "电影分类 - 屁屁影院",
            "description": "屁屁影院电影分类页面，按动作、喜剧、科幻、奇幻、剧情等类型浏览高清电影资源",
            "url": `https://www.pipicinema.com/category/${currentSlug}`,
            "isPartOf": {
              "@type": "WebSite",
              "name": "屁屁影院",
              "url": "https://www.pipicinema.com"
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.pipicinema.com" },
                { "@type": "ListItem", "position": 2, "name": "电影分类", "item": `https://www.pipicinema.com/category/${currentSlug}` }
              ]
            }
          })
        }}
      />
    </>
  );
}